import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Check, X, CreditCard, Heart } from 'lucide-react';
import { PRICING_PLANS, DONATION_TIERS } from '../data/pricingPlans';
import SEO from '../components/SEO';

export default function PricingPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  const handleSubscribe = (planId: string) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/pricing');
      return;
    }
    // Handle subscription
    console.log('Subscribe to plan:', planId);
  };

  const handleDonate = (tierId: string) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/pricing');
      return;
    }
    // Handle donation
    console.log('Donate tier:', tierId);
  };

  return (
    <>
      <SEO
        title="Pricing Plans & Support Options"
        description="Choose the perfect plan for your professional journey. From free trials to enterprise solutions, find the right fit for your needs."
        keywords={[
          'pricing plans',
          'professional subscription',
          'enterprise solutions',
          'content creation',
          'business tools',
          'analytics',
          'API access'
        ]}
      />

      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className={`text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Choose Your Plan
            </h1>
            <p className={`text-xl max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Find the perfect plan to support your professional journey
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center mt-8 space-x-4">
              <span className={`text-sm ${
                interval === 'monthly'
                  ? theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Monthly
              </span>
              <button
                onClick={() => setInterval(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full
                  transition-colors duration-200 ease-in-out focus:outline-none
                  ${interval === 'yearly'
                    ? 'bg-blue-600'
                    : theme === 'dark'
                      ? 'bg-gray-700'
                      : 'bg-gray-200'
                  }
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white shadow
                    transition-transform duration-200 ease-in-out
                    ${interval === 'yearly' ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
              <div className="flex items-center space-x-1">
                <span className={`text-sm ${
                  interval === 'yearly'
                    ? theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Yearly
                </span>
                <span className="text-sm text-green-500 font-medium">
                  (Save 20%)
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`
                  rounded-lg p-8
                  ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                  ${plan.tier === 'pro' ? 'ring-2 ring-blue-500' : ''}
                `}
              >
                <h3 className={`text-2xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {plan.name}
                </h3>
                <div className="flex items-baseline mb-6">
                  <span className={`text-4xl font-bold ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    ${interval === 'yearly' ? (plan.price * 0.8).toFixed(2) : plan.price}
                  </span>
                  <span className={`ml-2 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    /{interval === 'yearly' ? 'year' : 'month'}
                  </span>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                      <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  className={`
                    w-full px-6 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2
                    ${plan.tier === 'pro'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : theme === 'dark'
                        ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }
                  `}
                >
                  <CreditCard className="h-5 w-5" />
                  <span>{plan.tier === 'free' ? 'Start Free Trial' : 'Subscribe Now'}</span>
                </button>
              </div>
            ))}
          </div>

          {/* Donation Tiers */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <h2 className={`text-3xl font-bold mb-4 ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Support Our Mission
              </h2>
              <p className={`text-lg max-w-3xl mx-auto ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Help us empower tech talent in developing nations through your generous support
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {DONATION_TIERS.map((tier) => (
                <div
                  key={tier.id}
                  className={`
                    rounded-lg p-6
                    ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                  `}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-xl font-bold ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {tier.name}
                    </h3>
                    <span className={`text-2xl font-bold ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      ${tier.amount}
                    </span>
                  </div>

                  <p className={`mb-6 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {tier.description}
                  </p>

                  <div className="mb-6">
                    <h4 className={`text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Perks:
                    </h4>
                    <ul className="space-y-2">
                      {tier.perks.map((perk, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                          <span className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {perk}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => handleDonate(tier.id)}
                    className={`
                      w-full px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2
                      ${theme === 'dark'
                        ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }
                    `}
                  >
                    <Heart className="h-5 w-5 text-red-500" />
                    <span>Donate</span>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24">
            <h2 className={`text-3xl font-bold text-center mb-12 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  question: "What happens after my free trial?",
                  answer: "After your free trial ends, you can choose to upgrade to a paid plan or continue with limited features on the free tier."
                },
                {
                  question: "Can I switch plans?",
                  answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle."
                },
                {
                  question: "Is there a refund policy?",
                  answer: "We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied, contact our support team."
                },
                {
                  question: "What payment methods do you accept?",
                  answer: "We accept all major credit cards, PayPal, and bank transfers for enterprise plans."
                }
              ].map((faq, index) => (
                <div
                  key={index}
                  className={`p-6 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                  }`}
                >
                  <h3 className={`text-lg font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    {faq.question}
                  </h3>
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}